import { cx } from "../../utils/cx";
import { Tag } from "../ui";

// Shared by the admin and HLI granted students pages, which list the same granted bursary rows

export const trim = (value) => (typeof value === "string" ? value.trim() : value);
export const fullName = (l) => [trim(l.firstName), trim(l.lastName)].filter(Boolean).join(" ") || "—";

export const GRANTED_COLUMNS = [
  {
    key: "student",
    label: "Student",
    accessor: (l) => [fullName(l), l.registrationNumber, l.nationalId, l.email].filter(Boolean).join(" "),
    sortValue: fullName,
    render: (l) => (
      <div className="min-w-44">
        <p className="font-semibold text-slate-900">{fullName(l)}</p>
        <p className="text-xs text-slate-500">{[l.registrationNumber, l.nationalId].filter(Boolean).join(" · ")}</p>
      </div>
    ),
  },
  {
    key: "contact",
    label: "Contact",
    accessor: (l) => l.email,
    render: (l) => (
      <div>
        <p className="text-slate-800">{l.email || "—"}</p>
        <p className="text-xs text-slate-500">{l.phone}</p>
      </div>
    ),
  },
  {
    key: "college",
    label: "College",
    accessor: (l) => [l.collegeName, l.campusName].filter(Boolean).join(", "),
    render: (l) => (
      <div className="min-w-40">
        <p className="text-slate-800">{l.collegeName || "—"}</p>
        <p className="text-xs text-slate-500">{trim(l.campusName)}</p>
      </div>
    ),
  },
  {
    key: "qualification",
    label: "Qualification",
    accessor: (l) => l.qualificationName,
    render: (l) => (
      <div className="min-w-44">
        <p className="text-slate-800">{l.qualificationName || "—"}</p>
        <p className="text-xs text-slate-500">
          {[trim(l.levelOfStudy), l.programDuration && `${l.programDuration} years`].filter(Boolean).join(" · ")}
        </p>
      </div>
    ),
  },
  {
    key: "studyArea",
    label: "Study area",
    render: (l) => (l.studyArea ? <Tag color={l.studyArea === "STEM" ? "blue" : "brand"}>{l.studyArea}</Tag> : "—"),
  },
  {
    key: "period",
    label: "Study period",
    accessor: (l) => (l.startingYear && l.endingYear ? `${l.startingYear} – ${l.endingYear}` : "—"),
    sortValue: (l) => l.startingYear,
    className: "whitespace-nowrap",
  },
  {
    key: "products",
    label: "Products",
    sortable: false,
    accessor: (l) => (l.products ?? []).map((p) => p.name).join(", "),
    render: (l) =>
      l.products?.length ? (
        <ul className="flex flex-col gap-1 text-xs">
          {l.products.map((p) => (
            // Red dot: the product is on hold
            <li key={p.id} className="flex items-center gap-2" title={p.onHold ? "On hold" : "Active"}>
              <span className={cx("h-2 w-2 rounded-full", p.onHold ? "bg-red-400" : "bg-green-500")} />
              {p.name}
            </li>
          ))}
        </ul>
      ) : (
        "—"
      ),
  },
];

// Table dropdowns on fields the columns combine
export const GRANTED_FILTERS = [
  { key: "institutionName", label: "Institution", value: (l) => l.institutionName },
  { key: "gender", label: "Gender", value: (l) => l.gender },
  { key: "collegeName", label: "College", value: (l) => l.collegeName },
  { key: "levelOfStudy", label: "Level of study", value: (l) => trim(l.levelOfStudy) },
  { key: "qualificationName", label: "Qualification", value: (l) => l.qualificationName },
];

const DECIDERS = { HLI_ADMIN: "Institution", ADMIN: "HEC Admin", SELECTION: "Selection" };

// Every field, as exported to Excel
export const GRANTED_EXPORT_COLUMNS = [
  { key: "registrationNumber", label: "Registration Number" },
  { key: "nationalId", label: "National ID" },
  { key: "firstName", label: "First Name", accessor: (l) => trim(l.firstName) },
  { key: "lastName", label: "Last Name", accessor: (l) => trim(l.lastName) },
  { key: "gender", label: "Gender" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "indexNumber", label: "Index Number" },
  { key: "institutionName", label: "Institution" },
  { key: "collegeBrdCode", label: "College BRD Code" },
  { key: "collegeName", label: "College Name" },
  { key: "campusName", label: "Campus Name", accessor: (l) => trim(l.campusName) },
  { key: "qualificationCode", label: "Qualification Code" },
  { key: "qualificationName", label: "Qualification Name" },
  { key: "levelOfStudy", label: "Level of Study", accessor: (l) => trim(l.levelOfStudy) },
  { key: "studyArea", label: "Study Area" },
  { key: "academicYear", label: "Academic Year" },
  { key: "startingYear", label: "Starting Year" },
  { key: "endingYear", label: "Ending Year" },
  { key: "programDuration", label: "Program Duration" },
  { key: "durationPerYear", label: "Duration Per Year" },
  { key: "decidedBy", label: "Granted By", accessor: (l) => DECIDERS[l.decidedBy] ?? l.decidedBy },
  { key: "products", label: "Products", accessor: (l) => (l.products ?? []).map((p) => `${p.id} ${p.name}`).join(", ") },
];
